import {
  CalendarCheck,
  Plane,
  UserX,
  Headset,
  BookOpen,
  Crown,
  Users,
  ArrowRight,
} from "lucide-react";
import RadialOrbitalTimeline from "./RadialOrbitalTimeline";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const timelineData = [
  {
    id: 1,
    title: "10-Year Renewable Residency",
    date: "Jan 2024",
    content:
      "Enjoy a decade of guaranteed residency with seamless renewal options.",
    category: "Planning",
    icon: CalendarCheck,
    relatedIds: [2],
    status: "completed",
    energy: 100,
  },
  {
    id: 2,
    title: "No Sponsor Required",
    date: "Feb 2024",
    content: "Complete independence from traditional sponsorship requirements.",
    category: "Design",
    icon: UserX,
    relatedIds: [1, 3],
    status: "completed",
    energy: 90,
  },
  {
    id: 3,
    title: "Multiple Entry Visa during Processing",
    date: "Mar 2024",
    content: "Travel freely while your application is being processed.",
    category: "Development",
    icon: Plane,
    relatedIds: [2, 4],
    status: "in-progress",
    energy: 60,
  },
  {
    id: 4,
    title: "Fast-Tracked Application Support by Our Experts",
    date: "Apr 2024",
    content: "Our expert team ensures smooth processing and quick approvals.",
    category: "Testing",
    icon: Headset,
    relatedIds: [3, 5],
    status: "pending",
    energy: 30,
  },
  {
    id: 5,
    title: "Freedom to Live, Work & Study in the UAE",
    date: "May 2024",
    content:
      "Access world-class education, healthcare, and business opportunities.",
    category: "Release",
    icon: BookOpen,
    relatedIds: [4, 6],
    status: "pending",
    energy: 10,
  },
  {
    id: 6,
    title: "Family Sponsorship (including spouse, children, and parents)",
    date: "May 2024",
    content:
      "Bring your entire family including elderly parents for comprehensive coverage.",
    category: "Release",
    icon: Users,
    relatedIds: [5],
    status: "pending",
    energy: 10,
  },
];

export function RadialOrbitalTimelineDemo() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen w-full px-0 md:px-6 py-12">
      {/* badge */}
      <div className="flex justify-center flex-col items-center">
        <motion.span
          className="text-gold bg-white/10 px-3 py-2 rounded-full text-sm font-semibold mb-2 flex items-center gap-2"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Crown className="h-4 w-4 mr-2" />
          Premium UAE Residency Solution
        </motion.span>
        <span className="gradient-text text-3xl md:text-4xl lg:text-6xl font-bold">
          UAE Golden Visa
        </span>
      </div>

      <div className="container px-0 sm:px-5 mx-auto my-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* LEFT COLUMN - Text Content */}
        <div className="space-y-6 px-5 sm:px-0">
          <h1 className="text-2xl mt-5 md:mt-0 md:text-4xl font-bold tracking-tight">
            Unlock Long-Term
            <br />
            Residency in the UAE
          </h1>
          <p className="text-sm md:text-base text-gray-300 leading-relaxed">
            At{" "}
            <span className="text-white font-semibold">
              Scan Global Holdings
            </span>
            , we help investors, professionals, entrepreneurs, and families
            secure the prestigious
            <span className="text-gold text-xl font-bold">
              {" "}
              UAE Golden Visa
            </span>
            , offering you and your loved ones stability, freedom, and exclusive
            benefits.
          </p>
          <div className="text-gray-200">
            <h3 className="text-lg font-semibold mb-3 text-secondary">
              Apply through
            </h3>
            <ul className="list-disc pl-5 space-y-1 grid grid-cols-2 mb-4">
              <li>
                <span className="font-medium text-gold">
                  Real estate investment
                </span>
              </li>
              <li>
                <span className="font-medium text-gold">Business setup</span>
              </li>
              <li>
                <span className="font-medium text-gold">High-level talent</span>
              </li>
              <li>
                <span className="font-medium text-gold">
                  Academic achievement
                </span>
              </li>
            </ul>
            <div>
              Our team will guide you every step — from{" "}
              <span className="font-medium text-gold">eligibility check</span>{" "}
              to <span className="font-medium text-gold">final approval</span>
            </div>
          </div>

          {/* Optional: Add a call-to-action button */}
          <button
            onClick={() => navigate("/solutions/contact")}
            className="mt-4 inline-flex items-center px-6 py-3 bg-white hover:bg-white/80 text-slate-900 rounded-full text-sm font-semibold transition"
          >
            Get in Touch Today <ArrowRight className="w-4 h-4 ms-2" />
          </button>
        </div>

        {/* RIGHT COLUMN - Timeline */}
        <div className="w-full h-[550px] md:h-[600px]">
          <RadialOrbitalTimeline timelineData={timelineData} />
        </div>
      </div>
    </div>
  );
}

export default RadialOrbitalTimelineDemo;
